import { useState } from 'react'
import { Modal } from '@/shared/components/Modal'
import { PolicyOverrides } from '@/features/policies/components/PolicyOverrides'
import s from '@/shared/styles/policyTabs.module.css'

type PolicyTab = 'inherited' | 'custom'

/**
 * The policy-governance body, reused two ways: wrapped in its own `Modal` below for the
 * page-level "Org policies" button, and unwrapped as the Policies tab inside a provider's
 * ArtefactDetailModal (that dossier owns the dialog; embedding this would stack a second
 * <dialog>). Thin tabbed shell over PolicyOverrides:
 *  - per-provider: inherited-baseline overrides on one tab, identity-custom authoring (add/edit
 *    via the Rego editor, force-disable) on the other;
 *  - org mode: the applied org baseline (enable/disable, advisory/enforcing) on one tab, org
 *    policy authoring via the same Rego editor on the other.
 * PolicyOverrides is mounted ONCE with `visibleSection` driven by the active tab, so the policy
 * fetch and in-flight busy state survive tab switches.
 */
export function ProviderPoliciesPanel({ orgId, namespace, type, canAdmin }: {
  orgId: string
  namespace?: string
  type?: string
  canAdmin: boolean
}) {
  const [tab, setTab] = useState<PolicyTab>('inherited')
  const orgMode = !namespace || !type

  return (
    <>
      <div className={s.tabBar} role="tablist" aria-label="Policy sections">
        <TabButton label={orgMode ? 'Applied' : 'Inherited'} active={tab === 'inherited'} onClick={() => setTab('inherited')} />
        <TabButton label="Custom" active={tab === 'custom'} onClick={() => setTab('custom')} />
      </div>
      <PolicyOverrides
        orgId={orgId}
        artefactType="provider"
        instanceKey={orgMode ? null : `${namespace}/${type}`}
        canAdmin={canAdmin}
        visibleSection={tab}
      />
    </>
  )
}

/** Org-wide baseline policies, opened from the Providers page's "Org policies" button. The
 *  per-provider case is now the Policies tab inside ArtefactDetailModal (see ProviderPoliciesPanel
 *  above) rather than a modal of its own. */
export function ProviderPoliciesModal({ orgId, canAdmin, onClose }: {
  orgId: string
  canAdmin: boolean
  onClose: () => void
}) {
  return (
    <Modal
      track="provider_policies"
      title="Provider policies"
      kicker="// org baseline"
      maxWidth={720}
      onClose={onClose}
      footer={<div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button></div>}
    >
      <ProviderPoliciesPanel orgId={orgId} canAdmin={canAdmin} />
    </Modal>
  )
}

/** The PoliciesOverview tab affordance (role=tab + aria-selected), reusing its module CSS. */
function TabButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      role="tab"
      aria-selected={active}
      className={`${s.tab} ${active ? s.tabActive : ''}`}
      onClick={onClick}
    >
      {label}
    </button>
  )
}
